import { dummyDocumentPreparation } from './dummy-data/documentPreparation'
import { dummyEvaluationDetail } from './dummy-data/evaluationDetail'
import { dummyKinerjaProfile } from './dummy-data/kinerjaProfile'
import { dummyListGuruStates } from './dummy-data/listGuruStates'
import { dummyObservationImplementation } from './dummy-data/observationImplementation'
import { dummyPelaksanaanProgress } from './dummy-data/pelaksanaanProgress'
import { dummySupportingDocuments } from './dummy-data/supportingDocuments'
import { dummyTargetPerilaku } from './dummy-data/targetPerilaku'
import { dummyFollowUpDocument } from './dummy-data/followUpDocument'
import { dummyFollowUpEvaluationDocument } from './dummy-data/followUpEvaluationDocument'
import { dummyFollowUpReflectionDocument } from './dummy-data/followUpReflectionDocument'
import { dummyFollowUpTargets } from './dummy-data/followUpTargets'
import { dummyFollowUpReflectionEvaluationDocument } from './dummy-data/followUpReflectionEvaluationDocument'
import { dummyWorkBehavior } from './dummy-data/workBehavior'

import type { ResponseKinerjaProfil } from 'types/kinerja'
import type { CommonResponse } from 'types/api'
import type {
  IFollowUpTargetItemWithModules,
  ParamsUpsertFollowUpEvaluationDocument,
  ParamsUpsertFollowUpReflectionEvaluationDocument,
  ParamsUpsertObservationImplementation,
  ParamsUpsertWorkBehavior,
  ResponseEvaluationDetail,
  ResponseFollowUpDocument,
  ResponseFollowUpEvaluationDocument,
  ResponseFollowUpReflectionDocument,
  ResponseFollowUpReflectionEvaluationDocument,
  ResponseGetListGuruStates,
  ResponseGetObservationImplementation,
  ResponsePelaksanaanDocumentPreparation,
  ResponsePelaksanaanProgress,
  ResponsePelaksanaanTargetPerilaku,
  ResponseSupportingDocument,
  ResponseWorkBehavior,
} from 'types/kinerja-pelaksanaan'

interface DummyPengelolaanKinerja {
  listGuruStates: CommonResponse<ResponseGetListGuruStates>
  supportingDocuments: CommonResponse<ResponseSupportingDocument>
  evaluationDetail: CommonResponse<ResponseEvaluationDetail>
  pelaksanaanProgress: CommonResponse<ResponsePelaksanaanProgress>
  kinerjaProfile: ResponseKinerjaProfil
  targetPerilaku: CommonResponse<ResponsePelaksanaanTargetPerilaku[]>
  documentPreparation: CommonResponse<ResponsePelaksanaanDocumentPreparation>
  observationImplementation: CommonResponse<ResponseGetObservationImplementation>
  followUpDocument: CommonResponse<ResponseFollowUpDocument>
  followUpTargets: CommonResponse<IFollowUpTargetItemWithModules[]>
  followUpEvaluationDocument: CommonResponse<ResponseFollowUpEvaluationDocument>
  followUpReflectionDocument: CommonResponse<ResponseFollowUpReflectionDocument>
  followUpReflectionEvaluationDocument: CommonResponse<ResponseFollowUpReflectionEvaluationDocument>
  workBehavior: CommonResponse<ResponseWorkBehavior>
}

const localStorageKey = 'DEMO_PENGELOLAAN_KINERJA'

const initialData: DummyPengelolaanKinerja = {
  listGuruStates: dummyListGuruStates,
  supportingDocuments: dummySupportingDocuments,
  evaluationDetail: dummyEvaluationDetail,
  pelaksanaanProgress: dummyPelaksanaanProgress,
  kinerjaProfile: dummyKinerjaProfile,
  targetPerilaku: dummyTargetPerilaku,
  documentPreparation: dummyDocumentPreparation,
  observationImplementation: dummyObservationImplementation,
  followUpDocument: dummyFollowUpDocument,
  followUpTargets: dummyFollowUpTargets,
  followUpEvaluationDocument: dummyFollowUpEvaluationDocument,
  followUpReflectionDocument: dummyFollowUpReflectionDocument,
  followUpReflectionEvaluationDocument:
    dummyFollowUpReflectionEvaluationDocument,
  workBehavior: dummyWorkBehavior,
}

export const reinitialize = () => {
  window.localStorage.setItem(localStorageKey, JSON.stringify(initialData))
}

export const getDummyData = (): DummyPengelolaanKinerja => {
  const stringData = window.localStorage.getItem(localStorageKey)

  if (!stringData) {
    reinitialize()
  }

  return JSON.parse(window.localStorage.getItem(localStorageKey))
}

export const setDummyData = (key: keyof DummyPengelolaanKinerja, value) => {
  const currentData = getDummyData()
  window.localStorage.setItem(
    localStorageKey,
    JSON.stringify({ ...currentData, [key]: value })
  )
}

export const getListGuruStates = () => getDummyData().listGuruStates
export const getSupportingDocuments = () => getDummyData().supportingDocuments
export const getEvaluationDetail = () => getDummyData().evaluationDetail
export const getPelaksanaanProgress = () => getDummyData().pelaksanaanProgress
export const getKinerjaProfile = () => getDummyData().kinerjaProfile

export const getTargetPerilaku = () => getDummyData().targetPerilaku
export const getDocumentPreparation = () => getDummyData().documentPreparation
export const getObservationImplementation = () =>
  getDummyData().observationImplementation
export const upsertObservationImplementation = (
  params: ParamsUpsertObservationImplementation
) =>
  new Promise((resolve) => {
    setDummyData('observationImplementation', {
      data: params,
      meta: {},
    })

    if (params.state === 'SUBMITTED') {
      const currentPelaksanaanProgress = getPelaksanaanProgress()
      const currentEvaluationDetail = getEvaluationDetail()

      setDummyData('pelaksanaanProgress', {
        ...currentPelaksanaanProgress,
        data: {
          ...currentPelaksanaanProgress.data,
          progressPercentage: 50,
          state: 'FOLLOW_UP_EVALUATION',
        },
      })

      setDummyData('evaluationDetail', {
        ...currentEvaluationDetail,
        data: {
          ...currentEvaluationDetail.data,
          observation: {
            ...currentEvaluationDetail.data.observation,
            progress: 1,
            state: 'FOLLOW_UP_EVALUATION',
          },
        },
      })
    }

    resolve(getDummyData())
  })

export const getFollowUpTargets = () => getDummyData().followUpTargets
export const getFollowUpDocument = () => getDummyData().followUpDocument
export const getFollowUpEvaluationDocument = () =>
  getDummyData().followUpEvaluationDocument
export const getFollowUpReflectionDocument = () =>
  getDummyData().followUpReflectionDocument
export const getFollowUpReflectionEvaluationDocument = () =>
  getDummyData().followUpReflectionEvaluationDocument

export const upsertFollowUpEvaluationDocument = (
  params: ParamsUpsertFollowUpEvaluationDocument
) =>
  new Promise((resolve) => {
    setDummyData('followUpEvaluationDocument', {
      data: params,
      meta: {},
    })

    if (params.state === 'SUBMITTED') {
      const currentPelaksanaanProgress = getPelaksanaanProgress()
      const currentEvaluationDetail = getEvaluationDetail()

      setDummyData('pelaksanaanProgress', {
        ...currentPelaksanaanProgress,
        data: {
          ...currentPelaksanaanProgress.data,
          progressPercentage: 75,
          state: 'COMPLETED',
        },
      })

      setDummyData('evaluationDetail', {
        ...currentEvaluationDetail,
        data: {
          ...currentEvaluationDetail.data,
          observation: {
            ...currentEvaluationDetail.data.observation,
            progress: 2,
          },
        },
      })
    }

    resolve(getDummyData())
  })

export const upsertFollowUpReflectionEvaluationDocument = (
  params: ParamsUpsertFollowUpReflectionEvaluationDocument
) =>
  new Promise((resolve) => {
    setDummyData('followUpReflectionEvaluationDocument', {
      data: params,
      meta: {},
    })

    if (params.state === 'SUBMITTED') {
      const currentPelaksanaanProgress = getPelaksanaanProgress()
      const currentEvaluationDetail = getEvaluationDetail()

      setDummyData('pelaksanaanProgress', {
        ...currentPelaksanaanProgress,
        data: {
          ...currentPelaksanaanProgress.data,
          progressPercentage: 100,
          state: 'COMPLETED',
        },
      })

      setDummyData('evaluationDetail', {
        ...currentEvaluationDetail,
        data: {
          ...currentEvaluationDetail.data,
          observation: {
            ...currentEvaluationDetail.data.observation,
            progress: 3,
          },
          performancePractice: {
            ...currentEvaluationDetail.data.performancePractice,
            result: 'MEET_EXPECTATION',
          },
        },
      })
    }

    resolve(getDummyData())
  })

export const getworkBehavior = () => getDummyData().workBehavior

export const upsertWorkBehavior = (params: ParamsUpsertWorkBehavior) =>
  new Promise((resolve) => {
    const currentWorkBehavior = getworkBehavior()

    const newWorkBehavior: CommonResponse<ResponseWorkBehavior> = {
      ...currentWorkBehavior,
      data: {
        ...params,
        detail: {
          aspects: currentWorkBehavior.data.detail.aspects.map((currAspect) => {
            const newAspect = params.detail.aspects.find(
              (newAspect) => newAspect.aspectId === currAspect.aspectId
            )
            const impact = newAspect.impact
            const implementation = newAspect.implementation

            return {
              ...currAspect,
              impact,
              implementation,
            }
          }),
        },
      },
    }

    setDummyData('workBehavior', newWorkBehavior)

    const currentEvaluationDetail = getEvaluationDetail()

    setDummyData('evaluationDetail', {
      ...currentEvaluationDetail,
      data: {
        ...currentEvaluationDetail.data,
        workBehavior: {
          ...currentEvaluationDetail.data.workBehavior,
          ...(params.state === 'SUBMITTED'
            ? {
                progress:
                  currentEvaluationDetail.data.workBehavior.progress + 1,
                result: 'EXCEED_EXPECTATION',
              }
            : {}),
          state: params.state,
        },
      },
    })

    resolve(getDummyData())
  })
